import { EMAIL_TEMPLATE_SUGGESTION_LANGUAGES } from '@realadvisor/constants';

import { gql } from '@graphql/__generated__';
import type { GetEmailTemplateSuggestionsLanguage } from '@graphql/__generated__/graphql';
import type { EmailTemplateSuggestion } from '@modules/_shared/components/email-form/types';

export const GET_EMAIL_TEMPLATE_SUGGESTIONS = gql(/* GraphQL */ `
  query GetEmailTemplateSuggestions(
    $senderId: uuid
    $recipients: [uuid!]
    $language: GetEmailTemplateSuggestionsLanguage
    $leadId: uuid
  ) {
    get_email_template_suggestions(
      senderId: $senderId
      recipients: $recipients
      language: $language
      leadId: $leadId
    ) {
      suggestions
    }
  }
`);

export const toSuggestionLanguage = (
  language: string,
): GetEmailTemplateSuggestionsLanguage =>
  ((EMAIL_TEMPLATE_SUGGESTION_LANGUAGES as readonly string[]).includes(language)
    ? language
    : 'en') as GetEmailTemplateSuggestionsLanguage;

export type EmailSuggestionUser = {
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  emails?: Array<{ email?: string | null }>;
};

export const getFullName = (user: EmailSuggestionUser | null | undefined) =>
  user?.full_name ||
  [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();

export const getPrimaryEmail = (user: EmailSuggestionUser | null | undefined) =>
  user?.emails?.[0]?.email ?? '';

export const parseSuggestionsMap = (
  suggestions: unknown,
): EmailTemplateSuggestion[] =>
  Object.entries(
    (suggestions as Record<string, string | null> | null | undefined) ?? {},
  ).map(([id, value]) => ({
    id,
    name: value ?? '',
    value: value ?? '',
  }));

export const parseTemplateSuggestions = (suggestions: unknown) =>
  parseSuggestionsMap(suggestions).filter(s => !s.id.startsWith('#recipient'));

// The server emits `#recipient_N.*` only while To holds an N-th matched
// recipient, and the editor keeps a variable's text when its key is absent.
// The slots used in the body (`data-ra-var` ids) or the subject are listed so
// a removed recipient's ones resolve empty instead of keeping their name.
const RECIPIENT_SLOT_RE = /#recipient_\d+\.[A-Za-z0-9_]+/g;

export const recipientSlotIds = (texts: string[]): string[] => [
  ...new Set(texts.flatMap(text => text.match(RECIPIENT_SLOT_RE) ?? [])),
];

export const withRemovedRecipientsCleared = (
  map: Record<string, string>,
  slotIds: string[],
): Record<string, string> => {
  const missing = slotIds.filter(id => !(id in map));
  return missing.length === 0
    ? map
    : { ...map, ...Object.fromEntries(missing.map(id => [id, ''])) };
};

// The `#` menu hides variables without a value. In the mailbox, `#recipient.*`
// stays listed: it fills once To holds a CRM contact.
export const menuSuggestions = (
  suggestions: EmailTemplateSuggestion[],
  keepRecipient: boolean,
): EmailTemplateSuggestion[] =>
  suggestions.filter(
    suggestion =>
      (suggestion.value ?? suggestion.name).trim() !== '' ||
      (keepRecipient && suggestion.id.startsWith('#recipient.')),
  );
